/**
 * Image preparation for the cranial analysis tool (README 3.2).
 *
 * Decodes the chosen photo respecting EXIF orientation, applies the manual
 * 90-degree rotation and produces two JPEG copies with the same aspect ratio:
 * - display: best quality for the editor and the result (<= 2048 px);
 * - analysis: smaller copy sent to the backend (<= 1280 px, <= 1.5 MB).
 * The analysis copy also carries a reference grid (lib/referenceGrid.js); the
 * display copy stays clean and is the only one used by the local validation.
 * Re-encoding in canvas also drops EXIF/GPS metadata. Decode follows the same
 * approach as src/utils/imageResize.js (createImageBitmap with <img> fallback).
 */

import { drawReferenceGrid } from "./referenceGrid";

const DISPLAY_MAX_SIZE = 2048;
const DISPLAY_QUALITY = 0.92;
const ANALYSIS_MAX_SIZE = 1280;
const ANALYSIS_QUALITY = 0.85;
const ANALYSIS_MIN_QUALITY = 0.5;
const ANALYSIS_QUALITY_STEP = 0.1;
const ANALYSIS_MAX_BYTES = 1.5 * 1024 * 1024;

const normalizeRotation = (degrees) => {
  const value = Math.round((Number(degrees) || 0) / 90) * 90;
  return ((value % 360) + 360) % 360;
};

const getTargetSize = (width, height, maxSize) => {
  const largest = Math.max(width, height);
  if (!largest || largest <= maxSize) return { width, height };

  const ratio = maxSize / largest;
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
};

const decodeImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close && bitmap.close(),
      };
    } catch (e) {
      // Older browsers reject the option: fall back to <img> below.
    }
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve({
        source: image,
        width: image.naturalWidth,
        height: image.naturalHeight,
        release: () => URL.revokeObjectURL(url),
      });
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("IMAGE_DECODE_FAILED"));
    };

    image.src = url;
  });
};

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("IMAGE_ENCODE_FAILED"))),
      "image/jpeg",
      quality
    );
  });

/**
 * Draws the decoded source rotated into a canvas no larger than maxSize.
 */
const renderRotated = (decoded, rotation, maxSize) => {
  const swapsAxes = rotation === 90 || rotation === 270;
  const rotatedWidth = swapsAxes ? decoded.height : decoded.width;
  const rotatedHeight = swapsAxes ? decoded.width : decoded.height;
  const target = getTargetSize(rotatedWidth, rotatedHeight, maxSize);

  const canvas = document.createElement("canvas");
  canvas.width = target.width;
  canvas.height = target.height;

  const context = canvas.getContext("2d");
  const drawWidth = swapsAxes ? target.height : target.width;
  const drawHeight = swapsAxes ? target.width : target.height;

  context.translate(target.width / 2, target.height / 2);
  context.rotate((rotation * Math.PI) / 180);
  context.drawImage(decoded.source, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);

  return canvas;
};

const encodeAnalysisCopy = async (canvas) => {
  let quality = ANALYSIS_QUALITY;
  let blob = await canvasToBlob(canvas, quality);

  while (blob.size > ANALYSIS_MAX_BYTES && quality - ANALYSIS_QUALITY_STEP >= ANALYSIS_MIN_QUALITY) {
    quality -= ANALYSIS_QUALITY_STEP;
    blob = await canvasToBlob(canvas, quality);
  }

  return blob;
};

/**
 * @param {File|Blob} file Photo chosen or captured by the user.
 * @param {{ rotationDegrees?: number }} options Manual rotation, multiple of 90.
 * @returns {Promise<{ display: {blob, url, width, height}, analysis: {blob, width, height}, meta: object }>}
 */
export const prepareCranialImage = async (file, { rotationDegrees = 0 } = {}) => {
  const rotation = normalizeRotation(rotationDegrees);
  const decoded = await decodeImage(file);

  try {
    const displayCanvas = renderRotated(decoded, rotation, DISPLAY_MAX_SIZE);
    const displayBlob = await canvasToBlob(displayCanvas, DISPLAY_QUALITY);

    // The analysis copy is derived from the display canvas, so both keep the same ratio.
    const analysisCanvas = renderRotated(
      { source: displayCanvas, width: displayCanvas.width, height: displayCanvas.height },
      0,
      ANALYSIS_MAX_SIZE
    );
    drawReferenceGrid(analysisCanvas);
    const analysisBlob = await encodeAnalysisCopy(analysisCanvas);

    return {
      display: {
        blob: displayBlob,
        url: URL.createObjectURL(displayBlob),
        width: displayCanvas.width,
        height: displayCanvas.height,
      },
      analysis: {
        blob: analysisBlob,
        width: analysisCanvas.width,
        height: analysisCanvas.height,
      },
      meta: {
        originalWidth: decoded.width,
        originalHeight: decoded.height,
        analysisWidth: analysisCanvas.width,
        analysisHeight: analysisCanvas.height,
        displayWidth: displayCanvas.width,
        displayHeight: displayCanvas.height,
        rotationDegrees: rotation,
      },
    };
  } finally {
    if (decoded.release) decoded.release();
  }
};

/** Releases the object URL created for the display copy. */
export const revokePreparedImage = (image) => {
  if (image && image.display && image.display.url) {
    URL.revokeObjectURL(image.display.url);
  }
};

/**
 * RGBA pixels of a same-origin image (the display object URL), downscaled so
 * the largest side is at most maxSize. Used by the edge refinement (README 3.7).
 * @returns {Promise<{ pixels: Uint8ClampedArray, width: number, height: number }>}
 */
export const loadImagePixels = (url, maxSize) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const target = getTargetSize(image.naturalWidth, image.naturalHeight, maxSize);
        const canvas = document.createElement("canvas");
        canvas.width = target.width;
        canvas.height = target.height;
        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0, target.width, target.height);
        const { data } = context.getImageData(0, 0, target.width, target.height);
        resolve({ pixels: data, width: target.width, height: target.height });
      } catch (e) {
        reject(e);
      }
    };
    image.onerror = () => reject(new Error("IMAGE_DECODE_FAILED"));
    image.src = url;
  });
